import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const sections = [
  { id: 'who-we-are', title: '1. Who we are' },
  { id: 'scope', title: '2. Scope of this policy' },
  { id: 'information-we-collect', title: '3. Information we collect' },
  { id: 'how-we-use', title: '4. How we use your information' },
  { id: 'how-we-share', title: '5. How we share information' },
  { id: 'messaging', title: '6. Messaging channels (including Facebook Messenger)' },
  { id: 'overseas', title: '7. Overseas disclosure' },
  { id: 'security', title: '8. Storage and security' },
  { id: 'retention', title: '9. Retention' },
  { id: 'your-rights', title: '10. Your rights and choices' },
  { id: 'data-deletion', title: '11. How to request deletion of your data' },
  { id: 'cookies', title: '12. Cookies and similar technologies' },
  { id: 'children', title: '13. Children' },
  { id: 'changes', title: '14. Changes to this policy' },
  { id: 'contact', title: '15. Contact us' },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen font-sans">
      <Navbar />
      <main>
        <section className="relative pt-28 pb-14 bg-navy-900">
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-gold-400" />
              <span className="text-gold-400 text-sm font-medium tracking-widest uppercase">Legal</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-white/65 text-base leading-relaxed max-w-2xl">
              How Galaxy Housing collects, uses, stores, and protects personal information when you
              visit our websites, contact us, or message us on platforms such as Facebook Messenger.
            </p>
            <p className="text-white/45 text-sm mt-6">Last updated: 15 September 2026</p>
          </div>
        </section>

        <section className="py-16" style={{ background: 'rgb(246, 248, 252)' }}>
          <div className="max-w-3xl mx-auto px-6">
            <nav className="bg-white border border-navy-100 rounded-lg p-6 mb-10" aria-label="Privacy policy contents">
              <p className="text-navy-700/45 text-xs uppercase tracking-wide mb-3">On this page</p>
              <ul className="space-y-2">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="text-sm text-navy-800 hover:text-gold-500 transition-colors cursor-pointer"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="bg-white border border-navy-100 rounded-lg p-8 lg:p-10 space-y-10 text-navy-700/80 text-sm leading-relaxed">
              <section id="who-we-are">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">1. Who we are</h2>
                <p>
                  This Privacy Policy applies to <strong className="text-navy-800 font-semibold">Galaxy Housing</strong>{' '}
                  (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;), a provider of furnished shared accommodation
                  and related property management services in Australia.
                </p>
                <ul className="mt-4 space-y-2 list-disc pl-5">
                  <li>Postal address: PO Box 1035, Spit Junction NSW 2088, Australia</li>
                  <li>
                    Email:{' '}
                    <a href="mailto:rentals@galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      rentals@galaxyhousing.com.au
                    </a>
                  </li>
                  <li>
                    Phone:{' '}
                    <a href="tel:0483944617" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      0483 944 617
                    </a>
                  </li>
                  <li>
                    Websites:{' '}
                    <a href="https://galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      galaxyhousing.com.au
                    </a>
                    {' '}and our rentals portal at{' '}
                    <a href="https://rentals.galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      rentals.galaxyhousing.com.au
                    </a>
                  </li>
                </ul>
              </section>

              <section id="scope">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">2. Scope of this policy</h2>
                <p>
                  This policy explains how we handle personal information collected through our websites,
                  online enquiry forms, live chat, email, phone, SMS, WhatsApp, Facebook Page and Messenger,
                  Instagram (where used), our property management systems, and related communications with
                  prospective tenants, current tenants, and other contacts.
                </p>
                <p className="mt-3">
                  We handle personal information in accordance with the Australian Privacy Principles (APPs)
                  under the <em>Privacy Act 1988</em> (Cth), to the extent those obligations apply to us.
                </p>
              </section>

              <section id="information-we-collect">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">3. Information we collect</h2>
                <p>Depending on how you interact with us, we may collect:</p>
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>
                    <strong className="text-navy-800 font-semibold">Identity and contact details</strong> — name,
                    email address, phone number, postal address, and similar details you provide.
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Enquiry and tenancy information</strong> —
                    preferred locations, move-in timing, budget, occupancy needs, messages you send us, and
                    information needed to assess or manage a tenancy or application.
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Messaging platform identifiers</strong> —
                    for example a Facebook Page-scoped user ID (PSID), WhatsApp number, or other channel
                    identifiers needed to receive and reply to your messages.
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Technical information</strong> — IP address,
                    browser type, device information, pages visited, and approximate location derived from
                    network data when you use our websites or chat widgets.
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Records of communications</strong> —
                    transcripts and metadata of chats, emails, calls notes, and automated or human replies.
                  </li>
                </ul>
                <p className="mt-3">
                  We generally collect information directly from you. Where you message us via Meta products
                  (Facebook / Messenger), Meta may also share limited account or messaging metadata with us
                  as described in Meta&apos;s own policies and our app permissions.
                </p>
              </section>

              <section id="how-we-use">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">4. How we use your information</h2>
                <p>We use personal information to:</p>
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>Respond to enquiries about rooms, properties, and services</li>
                  <li>Qualify and follow up rental leads and applications</li>
                  <li>Provide customer support via website live chat, Messenger, WhatsApp, SMS, email, and phone</li>
                  <li>Operate and improve our websites, chat tools, and property management systems</li>
                  <li>Send service-related communications (for example vacancy updates you request)</li>
                  <li>Comply with legal obligations and protect our rights, property, and safety</li>
                </ul>
                <p className="mt-3">
                  We may use AI-assisted tools to help draft or suggest replies to common questions. Those tools
                  process conversation content only to generate responses for our team or automated assistants.
                  Final tenancy decisions and sensitive matters are handled by our staff where required.
                </p>
              </section>

              <section id="how-we-share">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">5. How we share information</h2>
                <p>We do not sell your personal information. We may disclose information to:</p>
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>
                    <strong className="text-navy-800 font-semibold">Service providers</strong> who host our
                    websites, property management software, databases, email, SMS/WhatsApp gateways, and
                    AI messaging assistance — strictly to perform services for us
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Platform providers</strong> such as Meta
                    (Facebook / Messenger / Instagram) when you choose to communicate with us on those channels
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Professional advisers and authorities</strong>{' '}
                    where reasonably necessary (for example lawyers, insurers, or regulators)
                  </li>
                  <li>
                    <strong className="text-navy-800 font-semibold">Other parties</strong> with your consent,
                    or where disclosure is required or authorised by law
                  </li>
                </ul>
              </section>

              <section id="messaging">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">
                  6. Messaging channels (including Facebook Messenger)
                </h2>
                <p>
                  If you message our Facebook Page, our systems may receive your message content and a
                  Page-scoped identifier so we can reply. Messages may be processed by our property management
                  platform and, where enabled, an automated assistant that answers common questions about
                  availability, pricing, locations, and application steps.
                </p>
                <p className="mt-3">
                  You can stop automated replies by asking to speak with a person, or by using Messenger
                  controls to end the conversation. Messaging on Meta platforms is also subject to Meta&apos;s
                  terms and privacy policy. We only use Messenger data to communicate with you about our
                  accommodation and related services — not for unrelated advertising audiences built from your
                  private messages.
                </p>
              </section>

              <section id="overseas">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">7. Overseas disclosure</h2>
                <p>
                  Some of our service providers (for example cloud hosting, messaging platforms, and AI
                  providers) may store or process data in countries outside Australia, including the United
                  States and other locations where those providers operate. Where we disclose personal
                  information overseas, we take reasonable steps to ensure it is handled consistently with
                  this policy and applicable Australian privacy requirements.
                </p>
              </section>

              <section id="security">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">8. Storage and security</h2>
                <p>
                  We use reasonable administrative, technical, and organisational measures to protect personal
                  information against misuse, interference, loss, and unauthorised access, modification, or
                  disclosure. No method of transmission or storage is completely secure; if you believe your
                  interaction with us has been compromised, please contact us promptly.
                </p>
              </section>

              <section id="retention">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">9. Retention</h2>
                <p>
                  We retain personal information for as long as needed for the purposes set out in this policy,
                  including to maintain enquiry and tenancy records, meet legal or accounting requirements, and
                  resolve disputes. When information is no longer required, we take reasonable steps to destroy
                  or de-identify it.
                </p>
              </section>

              <section id="your-rights">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">10. Your rights and choices</h2>
                <p>Subject to applicable law, you may request to:</p>
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>Access the personal information we hold about you</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Ask us to delete information we no longer need (see section 11)</li>
                  <li>Opt out of non-essential marketing communications</li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, email{' '}
                  <a href="mailto:rentals@galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                    rentals@galaxyhousing.com.au
                  </a>{' '}
                  with the subject line &ldquo;Privacy request&rdquo; and enough detail for us to identify your
                  records (for example the email, phone number, or Facebook profile name you used to contact us).
                </p>
              </section>

              <section id="data-deletion">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">
                  11. How to request deletion of your data
                </h2>
                <p>
                  If you used Facebook Messenger or other digital channels to contact us and want your
                  associated personal data deleted from our systems, you can:
                </p>
                <ol className="mt-3 space-y-2 list-decimal pl-5">
                  <li>
                    Email{' '}
                    <a href="mailto:rentals@galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      rentals@galaxyhousing.com.au
                    </a>{' '}
                    with the subject line &ldquo;Data deletion request&rdquo;.
                  </li>
                  <li>
                    Include the name or identifier you used when messaging us, the approximate dates of contact,
                    and the channel (for example Messenger, WhatsApp, website chat, or email).
                  </li>
                  <li>
                    We will verify the request where reasonable and delete or de-identify personal information
                    we no longer need to retain, usually within 30 days, unless we must keep certain records
                    for legal, accounting, or dispute-resolution reasons.
                  </li>
                </ol>
                <p className="mt-3">
                  Deleting data held by Meta (for example your Facebook account history) must be done through
                  Meta&apos;s own settings and tools. Our deletion process covers information we store in our
                  systems about your communications with Galaxy Housing.
                </p>
              </section>

              <section id="cookies">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">12. Cookies and similar technologies</h2>
                <p>
                  Our websites may use cookies, local storage, or similar technologies to operate the site,
                  remember preferences, and understand usage. You can control cookies through your browser
                  settings. Disabling cookies may affect some features (including chat widgets).
                </p>
              </section>

              <section id="children">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">13. Children</h2>
                <p>
                  Our services are directed to adults seeking accommodation. We do not knowingly collect
                  personal information from children under 16. If you believe a child has provided us with
                  personal information, please contact us so we can delete it.
                </p>
              </section>

              <section id="changes">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">14. Changes to this policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date at
                  the top of this page will change when we do. Continued use of our websites or messaging
                  channels after an update means you acknowledge the revised policy.
                </p>
              </section>

              <section id="contact">
                <h2 className="font-heading text-2xl font-bold text-navy-800 mb-3">15. Contact us</h2>
                <p>
                  For privacy questions, access requests, or complaints about how we handle personal
                  information, contact:
                </p>
                <div className="mt-4 bg-navy-50 border border-navy-100 rounded-lg p-5 space-y-1">
                  <p className="font-semibold text-navy-800">Galaxy Housing — Privacy</p>
                  <p>PO Box 1035, Spit Junction NSW 2088, Australia</p>
                  <p>
                    Email:{' '}
                    <a href="mailto:rentals@galaxyhousing.com.au" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      rentals@galaxyhousing.com.au
                    </a>
                  </p>
                  <p>
                    Phone:{' '}
                    <a href="tel:0483944617" className="text-navy-800 hover:text-gold-500 underline underline-offset-2">
                      0483 944 617
                    </a>
                  </p>
                </div>
                <p className="mt-4">
                  If you are not satisfied with our response, you may contact the Office of the Australian
                  Information Commissioner (OAIC) at{' '}
                  <a
                    href="https://www.oaic.gov.au"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-navy-800 hover:text-gold-500 underline underline-offset-2"
                  >
                    oaic.gov.au
                  </a>
                  .
                </p>
              </section>
            </article>

            <p className="text-center text-navy-700/50 text-sm mt-10">
              Questions?{' '}
              <Link to="/contact" className="text-navy-800 hover:text-gold-500 underline underline-offset-2 cursor-pointer">
                Contact us
              </Link>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
